import { useCallback } from 'react';
import { useSweepApi } from '../sweep/useSweepApi';
import type { ConfigurationCommentProperties, UserInputsByMetadataId } from '@server/user-inputs';
import type { ConfigurationComment } from '../../components/documentation/salesforce-documentation-card/annotations/comments/types';

export interface Tag {
  id: string;
  name: string;
  color: string;
}

interface PatchProps {
  commentId: string;
  commentProperties: ConfigurationCommentProperties;
  metadataId: string;
  crmOrgId: string;
}

export const useUserInputsApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    //tags are per account
    get_tags: useCallback(async () => {
      const response = await sweepApi.get(`/user-inputs/tags`);
      return response.data as Tag[];
    }, [sweepApi]),

    post_tag: useCallback(
      async (tag: Omit<Tag, 'id'>) => {
        const response = await sweepApi.post(`/user-inputs/tags`, tag);
        return response.data?.id;
      },
      [sweepApi],
    ),

    patch_tag: useCallback(
      async (tag: Tag) => {
        const response = await sweepApi.patch(`/user-inputs/tags/${tag.id}`, tag);
        return response.data;
      },
      [sweepApi],
    ),

    delete_tag: useCallback(
      async (tagId: string) => {
        const response = await sweepApi.delete(`/user-inputs/tags/${tagId}`);
        return response.data;
      },
      [sweepApi],
    ),

    get_tagConfigurations: useCallback(
      async (tagId: string, crmOrgId: string) => {
        const response = await sweepApi.get(`/user-inputs/tags/${tagId}?crmOrgId=${crmOrgId}`);
        return response.data;
      },
      [sweepApi],
    ),

    get_userConfigurations: useCallback(
      async (userId: string, crmOrgId: string) => {
        const response = await sweepApi.get(`/user-inputs/users/${userId}?crmOrgId=${crmOrgId}`);
        return response.data;
      },
      [sweepApi],
    ),

    delete_userConfigurations: useCallback(
      async (userId: string) => {
        const response = await sweepApi.delete(`/user-inputs/users/${userId}`);
        return response.data;
      },
      [sweepApi],
    ),

    get_userInputsForOrg: useCallback(
      async ({ crmOrgId }: { crmOrgId: string }) => {
        const response = await sweepApi.get(`/user-inputs?crmOrgId=${crmOrgId}`);
        return response.data as UserInputsByMetadataId;
      },
      [sweepApi],
    ),

    put_userInputsForConfiguration: useCallback(
      async ({
        crmOrgId,
        userInputs,
      }: {
        crmOrgId: string;
        userInputs: UserInputsByMetadataId;
      }) => {
        const [[metadataId, userInputData]] = Object.entries(userInputs);
        const response = await sweepApi.put(
          `/user-inputs/${metadataId}?crmOrgId=${crmOrgId}`,
          userInputData,
        );

        return response.data as UserInputsByMetadataId;
      },
      [sweepApi],
    ),

    get_configurationComments: useCallback(
      async (metadataId: string, crmOrgId: string) => {
        const response = await sweepApi.get(
          `/user-inputs/${metadataId}/comments?crmOrgId=${crmOrgId}`,
        );
        return response.data as ConfigurationComment[];
      },
      [sweepApi],
    ),

    post_configurationComment: useCallback(
      async (
        comment: Pick<ConfigurationComment, 'commentBody' & 'mentionedUserIds' & 'properties'>,
        metadataId: string,
        crmOrgId: string,
      ) => {
        const response = await sweepApi.post(
          `/user-inputs/${metadataId}/comments?crmOrgId=${crmOrgId}`,
          comment,
        );
        return response.data as ConfigurationComment;
      },
      [sweepApi],
    ),

    put_configurationComment: useCallback(
      async (comment: ConfigurationComment, metadataId: string, crmOrgId: string) => {
        const response = await sweepApi.put(
          `/user-inputs/${metadataId}/comments/${comment.id}?crmOrgId=${crmOrgId}`,
          comment,
        );
        return response.data as string;
      },
      [sweepApi],
    ),

    patch_configurationCommentProperties: useCallback(
      async ({ commentId, commentProperties, crmOrgId, metadataId }: PatchProps) => {
        await sweepApi.patch(
          `/user-inputs/${metadataId}/comments/${commentId}/properties?crmOrgId=${crmOrgId}`,
          commentProperties,
        );
      },
      [sweepApi],
    ),

    delete_configurationComment: useCallback(
      async (commentId: string, metadataId: string, crmOrgId: string) => {
        await sweepApi.delete(
          `/user-inputs/${metadataId}/comments/${commentId}?crmOrgId=${crmOrgId}`,
        );
      },
      [sweepApi],
    ),
  };
};
